/**
 * AdminFounderTab — Founding Business Control Center.
 *
 * Wraps the existing /api/founder/admin/* endpoints in a polished UI so
 * admins never have to hit raw JSON to manage the 100-position Founder
 * program. Fully honors the Founder business rules:
 *
 *   - Legacy + Stripe + Admin-granted Founders share the same 100-position
 *     cap (enforced server-side; UI reflects the authoritative count).
 *   - Manual grants require an explicit written reason + confirmation.
 *   - When 100/100 are claimed, self-service grant is disabled visually.
 *   - No public Founder Wall/Roll-of-Honor is built here (parked per spec).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Users, Search, Loader2, Trophy, ShieldCheck, AlertTriangle,
  Filter, Download, Plus, X as XIcon, CheckCircle2, FileText, Clock,
  Store, Mail, Calendar, DollarSign, ChevronRight, Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/App";
import AdminFounderPositionsCard from "@/components/AdminFounderPositionsCard";
import AdminFounderPriceCard from "@/components/AdminFounderPriceCard";
import AdminFounderIntegrityCard from "@/components/AdminFounderIntegrityCard";

const SOURCE_META = {
  LEGACY: { label: "Legacy", color: "bg-tbc-cream text-tbc-navy border border-tbc-navy/20" },
  STRIPE: { label: "Stripe", color: "bg-emerald-50 text-emerald-800 border border-emerald-200" },
  ADMIN: { label: "Admin", color: "bg-amber-50 text-amber-800 border border-amber-200" },
  UNSET: { label: "Unset", color: "bg-rose-50 text-rose-700 border border-rose-200" },
};

const FILTERS = [
  { key: "all", label: "All Founders" },
  { key: "LEGACY", label: "Legacy" },
  { key: "STRIPE", label: "Stripe" },
  { key: "ADMIN", label: "Admin Granted" },
  { key: "active_paid", label: "Active Paid Plan" },
  { key: "base_plan", label: "Base Plan" },
  { key: "issue", label: "Record Issues" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

function SourceBadge({ source }) {
  const meta = SOURCE_META[source || "UNSET"] || SOURCE_META.UNSET;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${meta.color}`}
      data-testid={`founder-source-badge-${source || "unset"}`}
    >
      {meta.label}
    </span>
  );
}

function KpiCard({ icon: Icon, label, value, sub, tone = "navy", testid }) {
  const tones = {
    navy: "bg-tbc-navy text-white",
    gold: "bg-tbc-gold text-tbc-navy",
    cream: "bg-white text-tbc-navy border border-tbc-navy/10",
    warn: "bg-amber-50 text-amber-900 border border-amber-200",
  };
  return (
    <div className={`rounded-xl p-4 ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider opacity-80">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <p className="mt-1 font-heading text-2xl">{value}</p>
      {sub && <p className="text-[11px] opacity-70 mt-0.5">{sub}</p>}
    </div>
  );
}

export default function AdminFounderTab() {
  const [audit, setAudit] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [grantOpen, setGrantOpen] = useState(false);
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [reconcileBusy, setReconcileBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, log] = await Promise.all([
        axios.get(`${API}/founder/admin/audit`, { withCredentials: true }),
        axios.get(`${API}/founder/admin/audit-log?limit=50`, { withCredentials: true }),
      ]);
      setAudit(a.data);
      setAuditLog(log.data?.entries || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load Founder audit");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Client-side filter/search across the audit list.
  // `founders` is memoized so the useMemo below doesn't reset every render.
  const founders = useMemo(() => audit?.founders || [], [audit?.founders]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return founders.filter((f) => {
      // Filter
      if (activeFilter === "LEGACY" && f.founder_source !== "LEGACY") return false;
      if (activeFilter === "STRIPE" && f.founder_source !== "STRIPE") return false;
      if (activeFilter === "ADMIN" && f.founder_source !== "ADMIN") return false;
      if (activeFilter === "active_paid") {
        const tier = (f.subscription_tier || "free").toLowerCase();
        const status = (f.subscription_status || "").toLowerCase();
        if (!(status === "active" && (tier === "premium" || tier === "standard"))) return false;
      }
      if (activeFilter === "base_plan") {
        const tier = (f.subscription_tier || "free").toLowerCase();
        const status = (f.subscription_status || "").toLowerCase();
        if (status === "active" && (tier === "premium" || tier === "standard")) return false;
      }
      if (activeFilter === "issue") {
        const issues = audit?.issues || {};
        const inDupN = (issues.duplicate_numbers || []).includes(f.founding_member_number);
        const inUnnumb = (issues.unnumbered_business_ids || []).includes(f.business_id);
        const inDupBiz = (issues.duplicate_business_ids || []).includes(f.business_id);
        const noSource = !f.founder_source;
        if (!(inDupN || inUnnumb || inDupBiz || noSource)) return false;
      }
      // Search
      if (!q) return true;
      return (
        (f.name || "").toLowerCase().includes(q) ||
        (f.owner_email || "").toLowerCase().includes(q) ||
        (f.owner_name || "").toLowerCase().includes(q) ||
        (f.business_id || "").toLowerCase().includes(q) ||
        String(f.founding_member_number || "").includes(q)
      );
    });
  }, [founders, search, activeFilter, audit?.issues]);

  const claimed = audit?.claimed ?? 0;
  const remaining = audit?.remaining ?? 100;
  const capFull = remaining <= 0;
  const bySource = audit?.by_source || {};

  const runReconcile = async () => {
    setReconcileBusy(true);
    try {
      const dry = await axios.post(
        `${API}/founder/admin/reconcile-positions?dry_run=true`,
        {},
        { withCredentials: true },
      );
      const { would_release = 0, would_tag = 0 } = dry.data || {};
      if (would_release === 0 && would_tag === 0) {
        toast.success("Registry already in sync — nothing to reconcile");
        return;
      }
      const ok = window.confirm(
        `Reconcile the Founder registry?\n\n` +
        `• Release ${would_release} orphan slot(s) (business no longer exists)\n` +
        `• Re-tag ${would_tag} business(es) with mirrored Founder flag/number\n\n` +
        `This writes to founder_positions + businesses + founder_grants (audited).`,
      );
      if (!ok) return;
      const r = await axios.post(
        `${API}/founder/admin/reconcile-positions?dry_run=false`,
        {},
        { withCredentials: true },
      );
      toast.success(`Reconciled: released ${r.data.released}, tagged ${r.data.tagged}. Claimed now ${r.data.claimed_after}/100.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reconcile failed");
    } finally {
      setReconcileBusy(false);
    }
  };

  const exportCsv = () => {
    if (!founders.length) return;
    const rows = [
      ["Founder #", "Business", "Owner Email", "Source", "Activation Date", "Plan", "Status", "First Paid Activation", "Stripe Sub ID", "Stripe Session"].join(","),
      ...founders.map((f) => [
        f.founding_member_number ?? "",
        `"${(f.name || "").replace(/"/g, '""')}"`,
        f.owner_email || "",
        f.founder_source || "",
        f.founder_activated_at || "",
        f.subscription_tier || "free",
        f.subscription_status || "",
        f.first_paid_plan_activated_at || "",
        f.stripe_subscription_id || "",
        f.founder_stripe_session_id || "",
      ].join(",")),
    ].join("\n");
    const blob = new Blob([rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `founding-businesses-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="admin-founder-tab">
      {/* ============ INTEGRITY (top-most — critical) ============ */}
      <AdminFounderIntegrityCard />

      {/* ============ POSITION MANAGER (100-slot registry) ============ */}
      <AdminFounderPositionsCard />

      {/* ============ FOUNDER PRICE CONTROL ============ */}
      <AdminFounderPriceCard />

      {/* ============ HEADER ============ */}
      <div className="bg-tbc-navy text-white rounded-2xl p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-wider text-tbc-gold flex items-center gap-1.5 font-semibold">
              <Trophy className="w-3.5 h-3.5" /> Founding Business Program
            </p>
            <h2 className="font-heading text-3xl mt-1" data-testid="founder-admin-title">
              {loading ? "—" : `${claimed} / 100`} <span className="text-tbc-gold text-lg font-normal">Claimed</span>
            </h2>
            <p className="text-sm text-white/70 mt-1" data-testid="founder-admin-remaining">
              {loading ? "Loading…" : `${remaining} positions remaining`}
              {capFull && " · Program is closed to new signups"}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              className="rounded-full border-white/30 bg-transparent text-white hover:bg-white hover:text-tbc-navy disabled:opacity-50"
              onClick={runReconcile}
              disabled={reconcileBusy}
              data-testid="founder-admin-reconcile-btn"
            >
              {reconcileBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              Reconcile Positions
            </Button>
            <Button
              variant="outline"
              className="rounded-full border-white/30 bg-transparent text-white hover:bg-white hover:text-tbc-navy"
              onClick={() => setBackfillOpen(true)}
              data-testid="founder-admin-backfill-btn"
            >
              <FileText className="w-4 h-4 mr-2" /> Audit / Backfill Records
            </Button>
            <Button
              className="rounded-full bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy font-semibold disabled:opacity-40"
              disabled={capFull}
              onClick={() => setGrantOpen(true)}
              data-testid="founder-admin-grant-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Grant Founder Status
            </Button>
          </div>
        </div>
      </div>

      {/* ============ KPI GRID ============ */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={Users} label="Total Founders" value={claimed} tone="navy" testid="kpi-total" />
        <KpiCard icon={Trophy} label="Positions Remaining" value={remaining} tone="gold" testid="kpi-remaining" />
        <KpiCard icon={Store} label="Legacy" value={bySource.LEGACY || 0} tone="cream" testid="kpi-legacy" />
        <KpiCard icon={DollarSign} label="Stripe-Paid" value={bySource.STRIPE || 0} tone="cream" testid="kpi-stripe" />
        <KpiCard icon={ShieldCheck} label="Admin Granted" value={bySource.ADMIN || 0} tone="cream" testid="kpi-admin" />
        <KpiCard icon={CheckCircle2} label="Active Paid" value={audit?.active_paid_founders || 0} tone="cream" testid="kpi-active-paid" sub="On Growth or Accelerator" />
        <KpiCard icon={Clock} label="On Base Plan" value={audit?.base_plan_founders || 0} tone="cream" testid="kpi-base" sub="Free tier / no active sub" />
        {(bySource.UNSET || 0) > 0 && (
          <KpiCard icon={AlertTriangle} label="Missing Source" value={bySource.UNSET} tone="warn" testid="kpi-unset" sub="Needs backfill" />
        )}
      </div>

      {/* ============ ISSUES CALLOUT ============ */}
      {audit?.issues && (
        (audit.issues.duplicate_numbers?.length || 0) +
        (audit.issues.unnumbered_business_ids?.length || 0) +
        (audit.issues.duplicate_business_ids?.length || 0) > 0
      ) && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 text-amber-900 p-4 text-sm" data-testid="founder-issues-callout">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 flex-none mt-0.5" />
            <div className="flex-1">
              <strong>Record issues detected:</strong>
              <ul className="mt-1 text-xs list-disc pl-4 space-y-0.5">
                {audit.issues.duplicate_numbers?.length > 0 && (
                  <li>Duplicate Founder numbers: {audit.issues.duplicate_numbers.join(", ")}</li>
                )}
                {audit.issues.unnumbered_business_ids?.length > 0 && (
                  <li>{audit.issues.unnumbered_business_ids.length} founder(s) missing a position number</li>
                )}
                {audit.issues.duplicate_business_ids?.length > 0 && (
                  <li>{audit.issues.duplicate_business_ids.length} duplicate business record(s)</li>
                )}
              </ul>
              <button
                type="button"
                onClick={() => setBackfillOpen(true)}
                className="mt-2 text-xs underline underline-offset-2 font-medium hover:text-amber-950"
              >
                Open Audit / Backfill →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============ SEARCH + FILTERS ============ */}
      <div className="bg-white rounded-2xl border border-tbc-navy/10 p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-tbc-navy/50" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search business, owner email, or Founder #"
              className="w-full pl-9 pr-3 py-2 rounded-full border border-tbc-navy/15 text-sm focus:outline-none focus:border-tbc-navy"
              data-testid="founder-search-input"
            />
          </div>
          <Button
            variant="outline"
            className="rounded-full border-tbc-navy/20 text-tbc-navy hover:bg-tbc-navy hover:text-white"
            onClick={exportCsv}
            disabled={!founders.length}
            data-testid="founder-export-csv-btn"
          >
            <Download className="w-4 h-4 mr-1.5" /> Export CSV
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          <Filter className="w-3.5 h-3.5 text-tbc-navy/40" />
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setActiveFilter(f.key)}
              className={`px-2.5 py-1 rounded-full border transition-colors ${
                activeFilter === f.key
                  ? "bg-tbc-navy text-white border-tbc-navy"
                  : "bg-white text-tbc-navy/70 border-tbc-navy/15 hover:border-tbc-navy"
              }`}
              data-testid={`founder-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* ============ FOUNDER TABLE ============ */}
      <div className="bg-white rounded-2xl border border-tbc-navy/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="founder-table">
            <thead className="bg-tbc-cream/50 text-xs uppercase tracking-wider text-tbc-navy/60">
              <tr>
                <th className="text-left px-3 py-2 whitespace-nowrap">#</th>
                <th className="text-left px-3 py-2">Business</th>
                <th className="text-left px-3 py-2">Source</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">Activation</th>
                <th className="text-left px-3 py-2">Plan</th>
                <th className="text-left px-3 py-2 whitespace-nowrap">Owner</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="text-center py-8 text-tbc-navy/50"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading founders…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={7} className="text-center py-8 text-tbc-navy/50">No Founders match your filter.</td></tr>
              )}
              {filtered.map((f) => (
                <tr
                  key={f.business_id}
                  onClick={() => setSelected(f)}
                  className="border-t border-tbc-navy/5 hover:bg-tbc-cream/30 cursor-pointer"
                  data-testid={`founder-row-${f.business_id}`}
                >
                  <td className="px-3 py-2 font-mono text-xs text-tbc-navy/70">
                    {f.founding_member_number ? `#${f.founding_member_number}` : <span className="text-rose-500">?</span>}
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-tbc-navy">{f.name || "—"}</div>
                    <div className="text-[10px] text-tbc-navy/50 font-mono">{f.business_id}</div>
                  </td>
                  <td className="px-3 py-2"><SourceBadge source={f.founder_source} /></td>
                  <td className="px-3 py-2 text-tbc-navy/70">{fmtDate(f.founder_activated_at)}</td>
                  <td className="px-3 py-2">
                    <span className="text-tbc-navy/80 capitalize">{f.subscription_tier || "free"}</span>
                    {f.subscription_status && (
                      <span className={`ml-1.5 text-[10px] px-1.5 rounded ${
                        f.subscription_status === "active"
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-tbc-cream text-tbc-navy/60"
                      }`}>
                        {f.subscription_status}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-tbc-navy/70 text-xs">{f.owner_email || "—"}</td>
                  <td className="px-3 py-2 text-tbc-navy/40"><ChevronRight className="w-4 h-4" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-3 py-2 text-[11px] text-tbc-navy/50 bg-tbc-cream/30">
          Showing {filtered.length} of {founders.length} · {audit?.grants_logged || 0} audit entries
        </div>
      </div>

      {/* ============ RECENT AUDIT LOG ============ */}
      <div className="bg-white rounded-2xl border border-tbc-navy/10 p-4">
        <div className="flex items-center gap-2 mb-2">
          <Clock className="w-4 h-4 text-tbc-navy/60" />
          <h3 className="font-heading text-lg text-tbc-navy">Recent Founder Audit Log</h3>
        </div>
        {auditLog.length === 0 ? (
          <p className="text-sm text-tbc-navy/50">No audit entries yet.</p>
        ) : (
          <ol className="space-y-1.5 text-xs">
            {auditLog.slice(0, 25).map((e, i) => (
              <li key={e.grant_id || i} className="flex items-start gap-2 text-tbc-navy/80" data-testid={`audit-log-entry-${i}`}>
                <span className="font-mono text-tbc-navy/40 whitespace-nowrap">{fmtDate(e.created_at)}</span>
                <SourceBadge source={e.source} />
                <span className="flex-1">
                  Founder <strong>#{e.founding_member_number}</strong> — {e.business_name || e.business_id}
                  {e.admin_reason && <span className="text-tbc-navy/50"> · {e.admin_reason}</span>}
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>

      {/* ============ MODALS ============ */}
      {selected && (
        <FounderDetailModal founder={selected} onClose={() => setSelected(null)} />
      )}
      {grantOpen && (
        <GrantFounderModal
          onClose={() => setGrantOpen(false)}
          onSuccess={() => { setGrantOpen(false); load(); }}
          remaining={remaining}
        />
      )}
      {backfillOpen && (
        <BackfillModal
          onClose={() => setBackfillOpen(false)}
          onSuccess={() => { setBackfillOpen(false); load(); }}
        />
      )}
    </div>
  );
}

// -------------------------------------------------------------------------
// Founder Detail Modal — full inspection view
// -------------------------------------------------------------------------
function FounderDetailModal({ founder, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      data-testid="founder-detail-modal"
    >
      <div
        className="w-full max-w-lg bg-white rounded-2xl overflow-y-auto max-h-[95vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-tbc-navy text-white p-5 relative">
          <button type="button" onClick={onClose} className="absolute top-3 right-3 text-white/60 hover:text-white" aria-label="Close" data-testid="founder-detail-close">
            <XIcon className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-tbc-gold">
            <Trophy className="w-3.5 h-3.5" />
            Founder {founder.founding_member_number ? `#${founder.founding_member_number}` : "(no #)"}
          </div>
          <h3 className="font-heading text-2xl mt-1">{founder.name}</h3>
          <p className="text-white/70 text-xs mt-1 font-mono">{founder.business_id}</p>
        </div>

        <div className="p-5 space-y-4 text-sm">
          <DetailSection title="Founder Information" icon={Trophy}>
            <Row label="Source"><SourceBadge source={founder.founder_source} /></Row>
            <Row label="Activation">{fmtDate(founder.founder_activated_at)}</Row>
            <Row label="Founder #">{founder.founding_member_number ?? "—"}</Row>
            <Row label="Discount active">{founder.founder_discount_active ? "Yes" : "No"}</Row>
          </DetailSection>

          <DetailSection title="Business & Owner" icon={Store}>
            <Row label="Owner"><span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {founder.owner_email || "—"}</span></Row>
            <Row label="Business ID">{founder.business_id}</Row>
          </DetailSection>

          <DetailSection title="Membership" icon={Calendar}>
            <Row label="Current plan"><span className="capitalize">{founder.subscription_tier || "free"}</span></Row>
            <Row label="Subscription status">{founder.subscription_status || "—"}</Row>
            <Row label="First paid activation">{fmtDate(founder.first_paid_plan_activated_at)}</Row>
          </DetailSection>

          <DetailSection title="Stripe" icon={DollarSign}>
            <Row label="Customer ID" mono>{founder.stripe_customer_id || "—"}</Row>
            <Row label="Subscription ID" mono>{founder.stripe_subscription_id || "—"}</Row>
            <Row label="Founder session" mono>{founder.founder_stripe_session_id || "—"}</Row>
            <Row label="Founder PI" mono>{founder.founder_stripe_payment_intent_id || "—"}</Row>
          </DetailSection>

          <div className="text-[11px] text-tbc-navy/50 flex items-start gap-1.5">
            <Info className="w-3.5 h-3.5 flex-none mt-0.5" />
            Founder status is permanent and independent of subscription state. Do not remove this record without recording the reason in the audit log.
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailSection({ title, icon: Icon, children }) {
  return (
    <div>
      <h4 className="text-[10px] uppercase tracking-wider text-tbc-navy/50 font-semibold flex items-center gap-1 mb-1.5">
        <Icon className="w-3 h-3" /> {title}
      </h4>
      <div className="rounded-lg bg-tbc-cream/40 border border-tbc-navy/5 divide-y divide-tbc-navy/5">
        {children}
      </div>
    </div>
  );
}

function Row({ label, mono, children }) {
  return (
    <div className="px-3 py-2 flex items-center justify-between gap-3 text-xs">
      <span className="text-tbc-navy/60">{label}</span>
      <span className={`text-right text-tbc-navy ${mono ? "font-mono text-[10px] break-all" : ""}`}>{children}</span>
    </div>
  );
}

// -------------------------------------------------------------------------
// Manual grant modal — search business, confirm, submit
// -------------------------------------------------------------------------
function GrantFounderModal({ onClose, onSuccess, remaining }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [target, setTarget] = useState(null);
  const [reason, setReason] = useState("");
  const [source, setSource] = useState("ADMIN");
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // Debounced search
  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await axios.get(`${API}/founder/admin/business-search`, {
          params: { q: query, limit: 15 },
          withCredentials: true,
        });
        setResults(r.data?.results || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Search failed");
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const doGrant = async () => {
    if (!target || !reason.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/founder/admin/grant`,
        { business_id: target.business_id, reason: reason.trim(), source },
        { withCredentials: true },
      );
      toast.success(`Founder status granted to ${target.name}`);
      onSuccess();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Grant failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      data-testid="founder-grant-modal"
    >
      <div className="w-full max-w-lg bg-white rounded-2xl overflow-hidden max-h-[95vh] flex flex-col">
        <div className="bg-tbc-navy text-white p-5 relative">
          <button type="button" onClick={onClose} className="absolute top-3 right-3 text-white/60 hover:text-white" aria-label="Close" data-testid="grant-modal-close">
            <XIcon className="w-5 h-5" />
          </button>
          <p className="text-xs uppercase tracking-wider text-tbc-gold flex items-center gap-1">
            <Plus className="w-3 h-3" /> Manual Grant
          </p>
          <h3 className="font-heading text-2xl mt-1">Grant Founding Business Status</h3>
          <p className="text-white/75 text-xs mt-2">{remaining} of 100 positions remain.</p>
        </div>

        <div className="p-5 space-y-4 flex-1 overflow-y-auto">
          {!confirming ? (
            <>
              <div>
                <label className="text-xs font-semibold text-tbc-navy/60 uppercase tracking-wider">Find business</label>
                <div className="relative mt-1">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-tbc-navy/50" />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Business name, ID, or owner email"
                    className="w-full pl-9 pr-3 py-2 rounded-lg border border-tbc-navy/15 text-sm focus:outline-none focus:border-tbc-navy"
                    autoFocus
                    data-testid="grant-search-input"
                  />
                </div>
              </div>
              <div className="border rounded-lg border-tbc-navy/10 divide-y divide-tbc-navy/5 max-h-64 overflow-y-auto">
                {searching && <div className="p-4 text-xs text-tbc-navy/50"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Searching…</div>}
                {!searching && query.trim().length < 2 && (
                  <div className="p-4 text-xs text-tbc-navy/50">Type at least 2 characters to search.</div>
                )}
                {!searching && query.trim().length >= 2 && results.length === 0 && (
                  <div className="p-4 text-xs text-tbc-navy/50">No non-Founder businesses match. (Existing Founders are hidden.)</div>
                )}
                {results.map((r) => (
                  <button
                    key={r.business_id}
                    type="button"
                    onClick={() => setTarget(r)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-tbc-cream/40 transition-colors ${
                      target?.business_id === r.business_id ? "bg-tbc-cream/60" : ""
                    }`}
                    data-testid={`grant-result-${r.business_id}`}
                  >
                    <div className="font-medium text-tbc-navy">{r.name}</div>
                    <div className="text-[11px] text-tbc-navy/60 flex items-center gap-2">
                      <span>{r.owner_email || "—"}</span>
                      {r.first_paid_plan_activated_at && (
                        <span className="text-amber-700">· Already activated paid plan</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>

              <div>
                <label className="text-xs font-semibold text-tbc-navy/60 uppercase tracking-wider">Grant source</label>
                <div className="mt-1 flex gap-2" data-testid="grant-source-toggle">
                  {[
                    { v: "ADMIN", label: "Admin", hint: "Discretionary gift" },
                    { v: "LEGACY", label: "Legacy", hint: "Grandfathered founder" },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setSource(opt.v)}
                      className={`flex-1 px-3 py-2 rounded-lg border text-xs text-left transition-colors ${
                        source === opt.v
                          ? "border-tbc-navy bg-tbc-cream/60 text-tbc-navy"
                          : "border-tbc-navy/15 text-tbc-navy/60 hover:border-tbc-navy/40"
                      }`}
                      data-testid={`grant-source-${opt.v.toLowerCase()}`}
                    >
                      <div className="font-semibold">{opt.label}</div>
                      <div className="text-[10px] opacity-80">{opt.hint}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-tbc-navy/60 uppercase tracking-wider">Reason (required)</label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Original pre-launch Founder — database backfill"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-tbc-navy/15 text-sm focus:outline-none focus:border-tbc-navy min-h-[70px]"
                  data-testid="grant-reason-input"
                />
              </div>
            </>
          ) : (
            <div className="space-y-3">
              <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3 text-amber-900 text-sm">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 flex-none mt-0.5" />
                  <div>
                    <p className="font-semibold">Confirm Founder grant</p>
                    <p className="text-xs mt-1">This will consume one of the 100 permanent Founder positions and cannot be silently reversed. An audit log entry will be recorded.</p>
                  </div>
                </div>
              </div>
              <div className="rounded-lg bg-tbc-cream/40 border border-tbc-navy/5 p-3 text-sm space-y-1">
                <p><strong>Business:</strong> {target.name}</p>
                <p className="text-xs text-tbc-navy/60"><strong>ID:</strong> <span className="font-mono">{target.business_id}</span></p>
                <p className="text-xs text-tbc-navy/60"><strong>Owner:</strong> {target.owner_email || "—"}</p>
                <p className="text-xs text-tbc-navy/60"><strong>Source:</strong> {source === "LEGACY" ? "LEGACY (Grandfathered)" : "ADMIN"}</p>
                <p className="text-xs text-tbc-navy/60"><strong>Reason:</strong> {reason.trim()}</p>
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-tbc-navy/10 p-4 flex flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={confirming ? () => setConfirming(false) : onClose}
            disabled={submitting}
            className="w-full sm:w-auto rounded-full border-tbc-navy/20 text-tbc-navy hover:bg-tbc-navy hover:text-white"
            data-testid="grant-modal-cancel"
          >
            {confirming ? "Back" : "Cancel"}
          </Button>
          {!confirming ? (
            <Button
              disabled={!target || !reason.trim()}
              onClick={() => setConfirming(true)}
              className="w-full sm:flex-1 rounded-full bg-tbc-navy hover:bg-tbc-navy/90 text-white disabled:opacity-40"
              data-testid="grant-modal-next"
            >
              Review Grant <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              disabled={submitting}
              onClick={doGrant}
              className="w-full sm:flex-1 rounded-full bg-tbc-gold text-tbc-navy hover:bg-tbc-gold/90 font-semibold"
              data-testid="grant-modal-confirm"
            >
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Granting…</> : "Confirm Grant"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// Backfill modal — audit first, then commit
// -------------------------------------------------------------------------
function BackfillModal({ onClose, onSuccess }) {
  const [dryRun, setDryRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [committing, setCommitting] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const runDryRun = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.post(
        `${API}/founder/admin/backfill-legacy?dry_run=true`,
        {},
        { withCredentials: true },
      );
      setDryRun(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Audit failed");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { runDryRun(); }, [runDryRun]);

  const commit = async () => {
    setCommitting(true);
    try {
      const r = await axios.post(
        `${API}/founder/admin/backfill-legacy?dry_run=false`,
        {},
        { withCredentials: true },
      );
      toast.success(`Backfilled ${r.data.migrated} legacy Founder record(s)`);
      onSuccess();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    } finally {
      setCommitting(false);
    }
  };

  const wouldMigrate = dryRun?.would_migrate || 0;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      data-testid="founder-backfill-modal"
    >
      <div className="w-full max-w-lg bg-white rounded-2xl overflow-hidden max-h-[95vh] flex flex-col">
        <div className="bg-tbc-navy text-white p-5 relative">
          <button type="button" onClick={onClose} className="absolute top-3 right-3 text-white/60 hover:text-white" aria-label="Close" data-testid="backfill-modal-close">
            <XIcon className="w-5 h-5" />
          </button>
          <p className="text-xs uppercase tracking-wider text-tbc-gold flex items-center gap-1"><FileText className="w-3 h-3" /> Audit / Backfill</p>
          <h3 className="font-heading text-2xl mt-1">Legacy Founder Backfill</h3>
          <p className="text-white/75 text-xs mt-2">Marks existing Founders without a source as <strong>LEGACY</strong> and logs them to the audit trail. Non-destructive — never charges anyone.</p>
        </div>
        <div className="p-5 space-y-4 flex-1 overflow-y-auto">
          {loading ? (
            <div className="text-sm text-tbc-navy/50"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Auditing existing Founder records…</div>
          ) : !confirming ? (
            <>
              <div className="rounded-lg bg-tbc-cream/50 border border-tbc-navy/10 p-4">
                <p className="text-xs uppercase tracking-wider text-tbc-navy/50 font-semibold">Proposed changes</p>
                <p className="mt-1 font-heading text-3xl text-tbc-navy">{wouldMigrate}</p>
                <p className="text-xs text-tbc-navy/60">record(s) will be marked <strong>LEGACY</strong> and logged.</p>
              </div>
              {wouldMigrate > 0 && (
                <div className="max-h-56 overflow-y-auto border border-tbc-navy/10 rounded-lg divide-y divide-tbc-navy/5">
                  {dryRun.candidates.map((c) => (
                    <div key={c.business_id} className="px-3 py-2 text-xs">
                      <div className="font-medium text-tbc-navy">{c.name || c.business_id}</div>
                      <div className="text-tbc-navy/50 font-mono text-[10px]">{c.business_id} · #{c.founding_member_number || "?"}</div>
                    </div>
                  ))}
                </div>
              )}
              {wouldMigrate === 0 && (
                <div className="text-sm text-tbc-navy/60"><CheckCircle2 className="w-4 h-4 inline text-emerald-600 mr-1" /> All Founder records already have a source. Nothing to migrate.</div>
              )}
            </>
          ) : (
            <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3 text-amber-900 text-sm">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 flex-none mt-0.5" />
                <div>
                  <p className="font-semibold">Confirm backfill</p>
                  <p className="text-xs mt-1">{wouldMigrate} record(s) will be updated. This action creates {wouldMigrate} audit-log entries but does not charge, delete, or transfer any Founder status.</p>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="border-t border-tbc-navy/10 p-4 flex flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={confirming ? () => setConfirming(false) : onClose}
            disabled={committing}
            className="w-full sm:w-auto rounded-full border-tbc-navy/20 text-tbc-navy hover:bg-tbc-navy hover:text-white"
            data-testid="backfill-modal-cancel"
          >
            {confirming ? "Back" : "Close"}
          </Button>
          {!loading && wouldMigrate > 0 && !confirming && (
            <Button
              onClick={() => setConfirming(true)}
              className="w-full sm:flex-1 rounded-full bg-tbc-navy hover:bg-tbc-navy/90 text-white"
              data-testid="backfill-modal-next"
            >
              Review Changes <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
          {confirming && (
            <Button
              disabled={committing}
              onClick={commit}
              className="w-full sm:flex-1 rounded-full bg-tbc-gold text-tbc-navy hover:bg-tbc-gold/90 font-semibold"
              data-testid="backfill-modal-confirm"
            >
              {committing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Committing…</> : `Commit ${wouldMigrate} Record(s)`}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
